import { useEffect, useState } from "react";
import { useParams, useNavigate, Link, Outlet, useLocation } from "react-router-dom";
import { tplApi, getToken, clearToken } from "@/lib/templateApi";
import {
  LayoutDashboard, Image, Users, MapPin, Clock, UtensilsCrossed,
  Scissors, Calendar, Images, MessageSquareQuote, Bell, FileText,
  Gift, Mail, LogOut, ChevronRight, ExternalLink, Home,
} from "lucide-react";

const MODULE_META = {
  hero:          { label: "Hero Section",   icon: Image,               phase: 1 },
  about:         { label: "About",          icon: Users,               phase: 2 },
  contact:       { label: "Contact",        icon: MapPin,              phase: 2 },
  hours:         { label: "Hours",          icon: Clock,               phase: 2 },
  footer:        { label: "Footer",         icon: LayoutDashboard,     phase: 2 },
  menu:          { label: "Menu",           icon: UtensilsCrossed,     phase: 3 },
  services:      { label: "Services",       icon: Scissors,            phase: 3 },
  staff:         { label: "Staff",          icon: Users,               phase: 3 },
  gallery:       { label: "Gallery",        icon: Images,              phase: 3 },
  booking:       { label: "Booking",        icon: Calendar,            phase: 4 },
  testimonials:  { label: "Testimonials",   icon: MessageSquareQuote,  phase: 4 },
  announcements: { label: "Announcements",  icon: Bell,                phase: 4 },
  blog:          { label: "Blog",           icon: FileText,            phase: 4 },
  gift_cards:    { label: "Gift Cards",     icon: Gift,                phase: 4 },
  newsletter:    { label: "Newsletter",     icon: Mail,                phase: 4 },
};

export default function AdminLayout() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = getToken();
    if (!token) {
      navigate(`/t/${slug}/admin/login`, { replace: true });
      return;
    }
    tplApi
      .getMe()
      .then((res) => {
        if (res.tenant?.slug !== slug) {
          clearToken();
          navigate(`/t/${slug}/admin/login`, { replace: true });
          return;
        }
        setTenant(res.tenant);
      })
      .catch(() => {
        clearToken();
        navigate(`/t/${slug}/admin/login`, { replace: true });
      })
      .finally(() => setLoading(false));
  }, [slug, navigate]);

  if (loading || !tenant) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-slate-600 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  const modules = (tenant.modules_enabled || []).filter((m) => MODULE_META[m]);
  const activeModule = location.pathname.split("/").pop();

  return (
    <div className="min-h-screen bg-[#0F172A] flex" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className="w-60 bg-[#0B1120] border-r border-white/[0.06] flex flex-col shrink-0">
        <div className="p-5 border-b border-white/[0.06]">
          <Link to={`/t/${slug}`} target="_blank" className="text-sm font-bold text-white block">
            {tenant.name}
          </Link>
          <p className="text-[11px] text-slate-600 mt-0.5">Admin Panel</p>
        </div>
        <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-auto">
          <SidebarLink
            to={`/t/${slug}/admin`}
            icon={LayoutDashboard}
            label="Dashboard"
            active={location.pathname === `/t/${slug}/admin`}
          />
          <p className="text-[10px] text-slate-600 uppercase tracking-wider px-3 pt-4 pb-1">Modules</p>
          {modules.map((m) => {
            const meta = MODULE_META[m];
            const ready = meta.phase <= 2;
            return (
              <SidebarLink
                key={m}
                to={ready ? `/t/${slug}/admin/${m}` : null}
                icon={meta.icon}
                label={meta.label}
                active={activeModule === m}
                disabled={!ready}
              />
            );
          })}
        </nav>
        <div className="p-3 border-t border-white/[0.06]">
          <button
            onClick={() => { clearToken(); navigate(`/t/${slug}/admin/login`); }}
            className="flex items-center gap-2 text-slate-500 hover:text-red-400 text-sm w-full px-3 py-2 rounded-md transition-colors"
            data-testid="admin-logout-btn"
          >
            <LogOut size={15} /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-auto">
        {/* Breadcrumb + live site link */}
        <div className="px-6 py-3 border-b border-white/[0.06] flex items-center justify-between bg-[#0B1120]">
          <div className="flex items-center gap-1.5 text-xs">
            <Link to={`/t/${slug}/admin`} className="text-slate-500 hover:text-slate-300 transition-colors">Admin</Link>
            {activeModule && MODULE_META[activeModule] && (
              <>
                <span className="text-slate-700">/</span>
                <span className="text-slate-300 font-medium">{MODULE_META[activeModule]?.label}</span>
              </>
            )}
          </div>
          <a href={`/t/${slug}`} target="_blank" rel="noopener noreferrer"
            className="text-[11px] text-slate-500 hover:text-orange-400 transition-colors flex items-center gap-1"
            data-testid="admin-view-live-link"
          >
            <ExternalLink size={11} /> View live site
          </a>
        </div>
        <Outlet context={{ tenant, slug }} />
      </main>
    </div>
  );
}

function SidebarLink({ to, icon: Icon, label, active, disabled }) {
  const cls = `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors ${
    active
      ? "bg-orange-500/10 text-orange-400"
      : disabled
      ? "text-slate-700 cursor-default"
      : "text-slate-400 hover:bg-white/[0.03] hover:text-white"
  }`;

  if (!to || disabled) {
    return (
      <div className={cls}>
        <Icon size={15} strokeWidth={1.5} />
        <span className="flex-1">{label}</span>
        {disabled && <span className="text-[9px] text-slate-700 bg-slate-800 px-1.5 py-0.5 rounded">Soon</span>}
      </div>
    );
  }

  return (
    <Link to={to} className={cls}>
      <Icon size={15} strokeWidth={1.5} />
      <span className="flex-1">{label}</span>
      <ChevronRight size={12} className="text-slate-700" />
    </Link>
  );
}
